// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { LANAC_DEBUG, useEffect, useRef } from "../00-boot.jsx";
import { PRESENCE_RATTRAPAGE_MS } from "./usePresence.jsx";
import { appliquerReprise, REPRISE_MARGE_MS } from "./reprise.jsx";
import { isClosedListingStatus } from "../01-taxonomy-cities.jsx";
import { SEED_CONVOS, genId } from "../04-i18n-dict.jsx";
import { listingFromDB } from "../05-ui-atoms-theme.jsx";
import { rpcReprise, richNoticeFromRow, INBOX_PREVIEW_SELECT_ADMIN, lanacSemerFiligraneDuCache, CACHE_CONVOS_KEY, CACHE_CONVOSHELLS_KEY, CACHE_PREVIEWS_KEY, CACHE_PREVIEWSYNC_KEY, lanacPreviewSyncOn, INBOX_CONVO_ID_CHUNK_SIZE, INBOX_CONVO_SELECT, INBOX_PREVIEW_SELECT, INBOX_RECENT_MESSAGE_LIMIT, convoActivityTs, convoFromDB, convoUnreadCount, dealFromDB, isMsgUpgrade, lanacConvoFlagsMap, lanacInboxPager, lanacInboxPagingOn, lanacInboxServerCounters, lanacReadSeqMap, lanacResetPerUserState, mergeConvoMessageLists, messageFromDB, messageUnreadForUser, normalizeConvoForInbox, reconcileMessageVersion, rpcInboxCounters, rpcInboxPage, rpcMyFailedOfferCounts, rpcMyUnreadCounts, rpcSetConversationFlags, setLanacInboxServerCounters } from "../07-trust-moderation.jsx";
import { lanacSortie, lanacSupabaseClient } from "../08-supabase-client.jsx";
// ═══ V3-T6 · useRealtimeInbox — LE cluster realtime, d'un seul bloc ══════════
//  Contient les DEUX effets qui portent la livraison temps réel, déplacés
//  VERBATIM (codemod, jamais retapés) depuis App() :
//    1. l'effet MESSAGES (559 l.) : hydratation de l'inbox, catchUp, complétude
//       M5, dédup, et la DÉFINITION des logiques d'application (deal / convo /
//       message) exposées via les ponts ref ci-dessous ;
//    2. le canal privé `lanac:user:<me>` (67 l.) : reçoit les 4 events du
//       serveur et DÉCLENCHE ces logiques par les ponts, sans re-souscrire.
//
//  ⚠ NON DÉCOUPÉ, ET C'EST VOLONTAIRE : dédup, ordre par seq et complétude M5
//  partagent une seule closure — cette cohésion EST la fiabilité du système.
//  Ce qu'on rend explicite, c'est sa FRONTIÈRE (le contrat `ports` ci-dessous,
//  mesuré par tools/effect-ownership.mjs, pas deviné), pas son intérieur.
//
//  Les 5 ponts ref n'existent QUE pour ces 2 effets (vérifié : aucun autre
//  lecteur dans App) → ils sont déclarés ici, chez eux.
/** @param {any} me @param {{convosRef,loadConvosRef,msgToastPrefRef,setConvos,setConvosLoading,setMsgToast,setDeals,setOfferFailCounts,feedItemsRef,itemsRef,selRef,setFeedItems,setItems,setSel,viewRef,setNotices,setNoticesHasMore:any}} ports */
function useRealtimeInbox(me, ports){
  const {convosRef,loadConvosRef,msgToastPrefRef,setConvos,setConvosLoading,setMsgToast,
    setDeals,setOfferFailCounts,setInboxServerCounters,
    feedItemsRef,itemsRef,selRef,setFeedItems,setItems,setSel,
    viewRef,
    setNotices,setNoticesHasMore}=ports;
  // Realtime #3 Phase 2a — pont inter-effets : l'effet MESSAGES définit la logique
  // d'application d'un deal (reconcileConfirmedDeal vit dans SA closure) ; le canal privé
  // `lanac:user:<me>` la déclenche via ce ref stable, sans re-souscrire ni fuir de scope.
  const applyDealRef=useRef(null);
  // Phase 2b — même pont pour les conversations : mergeConvo (auto-gardé par canSeeConvo,
  // idempotent par id) vit dans la closure de l'effet MESSAGES.
  const mergeConvoRef=useRef(null);
  // A36 · 2d : ponts vers l'effet NOTIFICATIONS — appliquer une notification venue de la reprise (même voie que le direct),
  //  recharger la liste quand la reprise a débordé sa borne.
  const applyNotifRef=useRef(null);
  const reloadNoticesRef=useRef(null);
  // Phase 2c — pont pour la voie INBOX des messages : {insert,update} définis dans l'effet
  // MESSAGES (toast dédupliqué par id), déclenchés par le canal `lanac:user:<me>`.
  const applyMessageRef=useRef(null);
  // A1 (déconnexion en arrière-plan) — ponts pour PILOTER le canal de livraison depuis
  // l'effet MESSAGES : onglet caché ⇒ on retire les canaux persistants pour relâcher le
  // WebSocket (l'utilisateur backgroundé cesse de compter dans le plafond de connexions
  // Supabase) ; retour au 1er plan ⇒ on les recrée. teardown retire, rearm recrée (idempotent).
  const bcastRearmRef=useRef(null);
  const bcastTeardownRef=useRef(null);
  useEffect(()=>{
    const client=lanacSupabaseClient();
    // ⚠️ EN PREMIER, AVANT TOUT LE RESTE — y compris la sortie anticipée ci-dessous et
    //  l'hydratation du cache (l. ~105, qui appelle normalizeConvoForInbox). Purge l'état
    //  de MODULE par-utilisateur si le compte a changé : sans elle, les drapeaux
    //  archivé/masqué du compte PRÉCÉDENT s'appliquent aux conversations du suivant
    //  pendant toute la fenêtre de chargement (voir le bloc d'invariant en 07).
    //  No-op si le compte n'a pas changé ⇒ un simple re-rendu ne jette RIEN.
    lanacResetPerUserState(me?.id||null);
    LANAC_DEBUG&&console.info("[LANAC] messaging effect triggered, me =",{id:me?.id||null,role:me?.role||null,hasClient:!!client,at:new Date().toISOString()});
    if(!client||!me?.id){
      LANAC_DEBUG&&console.info("[LANAC] messaging effect early exit",{hasClient:!!client,hasMe:!!me?.id});
      setConvos([]);
      setConvosLoading(false);
      return;
    }
    let cancelled=false;
    const pendingFetches=new Set();
    // Fix D egress (12/07) : horodatage du dernier chargement inbox. Sert UNIQUEMENT à throttler le refetch
    // déclenché par le retour au 1er plan (visibilitychange) — jamais le boot ni le pull-to-refresh, qui
    // appellent loadConvos directement. Écrit au démarrage de CHAQUE loadConvos (donc un refresh manuel ou un
    // boot récent décale d'autant la fenêtre → aucun rechargement redondant juste après).
    let lastConvosLoadAt=0;
    LANAC_DEBUG&&console.info("[LANAC] messaging effect setConvosLoading(true)",{meId:me.id,role:me.role});
    setConvosLoading(true);
    const canSeeConvo=c=>!!c&&((c.participants||[]).includes(me.id)||me.role==="Admin");
    const seedConvoIds=new Set(SEED_CONVOS.map(c=>c.id));
    //  (lot D · D6-b, 31/10/2026, revue AVANT m4) le cache est lu UNE fois, ici, dans le corps de l'effet (après la purge par compte, jamais dans
    //   le callback de setConvos) : il SÈME le filigrane — un boot depuis le cache sait déjà ce qui est lu. Sans lui, tout message lu par le
    //   filigrane (read=false en base depuis le 15/07) comptait non lu jusqu'à la réponse de l'inbox : le badge sautait puis retombait.
    //   Jamais en recul (max) ; la réponse serveur (fetchInboxConversations) reste un `set` — la vérité, qui corrige un cache en avance.
    let convosDuCache=null;
    try{const raw=typeof localStorage!=="undefined"&&localStorage.getItem(CACHE_CONVOS_KEY(me.id));if(raw){const lus=JSON.parse(raw);if(Array.isArray(lus))convosDuCache=lus;}}catch(e){}
    lanacSemerFiligraneDuCache(convosDuCache);
    setConvos(prev=>{
      const real=prev.filter(c=>!seedConvoIds.has(c.id)&&canSeeConvo(c));
      if(real.length) return real; // déjà des vraies données → on n'écrase pas
      // hydratation cache local : affichage instantané avant la réponse serveur (loadConvos fusionnera/écrasera ensuite par id)
      if(convosDuCache&&convosDuCache.length){try{return convosDuCache.filter(canSeeConvo).map(c=>normalizeConvoForInbox(c,me.id));}catch(e){}}
      return real;
    });
    const mergeConvoModels=(rows,label="unknown")=>{
      if(cancelled){LANAC_DEBUG&&console.info("[LANAC] mergeConvoModels skipped: cancelled",{label});return;}
      const incoming=(rows||[]).filter(canSeeConvo).map(c=>normalizeConvoForInbox(c,me.id));
      LANAC_DEBUG&&console.info("[LANAC] mergeConvoModels called",{label,inputRows:Array.isArray(rows)?rows.length:null,incomingRows:incoming.length,sampleIds:incoming.slice(0,5).map(c=>c.id)});
      if(!incoming.length){
        console.warn("[LANAC] mergeConvoModels: 0 incoming rows after canSeeConvo",{label,meId:me.id,role:me.role});
        setConvos(prev=>prev.filter(canSeeConvo));
        return;
      }
      setConvos(prev=>{
        const prevVisible=prev.filter(canSeeConvo);
        const byId=new Map(prevVisible.map(c=>[c.id,c]));
        incoming.forEach(next=>{
          const current=byId.get(next.id);
          byId.set(next.id,current
            ?normalizeConvoForInbox({...current,...next},me.id,{
              messages:mergeConvoMessageLists(current.messages||[],next.messages||[]),
              unreadCount:(next.messages||[]).length>0?next.unreadCount:current.unreadCount,
              messagesLoaded:!!(current.messagesLoaded||next.messagesLoaded),
            })
            :{...next,messages:next.messages||[]});
        });
        const incomingIds=new Set(incoming.map(c=>c.id));
        const mergedIncoming=incoming.map(c=>byId.get(c.id)).filter(Boolean);
        const localOnly=prevVisible.filter(c=>!incomingIds.has(c.id));
        const nextState=[...mergedIncoming,...localOnly];
        LANAC_DEBUG&&console.info("[LANAC] mergeConvoModels state update",{label,prevTotal:prev.length,prevVisible:prevVisible.length,nextTotal:nextState.length,incomingRows:incoming.length,localOnly:localOnly.length});
        return nextState;
      });
    };
    const mergeConvo=row=>{
      const next=convoFromDB(row);
      if(!next) return;
      mergeConvoModels([next],"realtime-conversation");
    };
    mergeConvoRef.current=mergeConvo; // pont vers le canal `lanac:user:<me>` (effet NOTIF) — Phase 2b
    const mergeMessages=rows=>{
      if(cancelled||!Array.isArray(rows)||!rows.length) return;
      const grouped=new Map();
      rows.forEach(row=>{
        const msg=messageFromDB(row);
        const cid=row?.convo_id;
        if(!msg||!cid) return;
        if(!grouped.has(cid)) grouped.set(cid,[]);
        grouped.get(cid).push(msg);
      });
      if(!grouped.size) return;
      setConvos(p=>p.map(c=>{
        const incoming=grouped.get(c.id);
        if(!incoming?.length) return c;
        const messages=mergeConvoMessageLists(c.messages||[],incoming);
        return normalizeConvoForInbox(c,me.id,{messages});
      }));
    };
    const applyUnreadCounts=counts=>{
      if(cancelled) return;
      setConvos(prev=>prev.map(c=>canSeeConvo(c)?normalizeConvoForInbox(c,me.id,{unreadCount:counts.get(c.id)||0}):c));
    };
    // Lot 7 (Phase 3b) : agrégat {annonce → nb d'offres échouées} de l'acheteur, une fois au boot. Un seul appel
    // RLS-scopé (my_failed_offer_counts partage is_failed_offer avec le trigger → même définition), vide pour la
    // plupart. Admin exclu (pas d'acheteur). Résultat rangé en snapshot ; le pré-contrôle prend max(snapshot,scan).
    const fetchMyFailedOfferCounts=async()=>{
      if(!client||!me?.id||me.role==="Admin"||cancelled) return;
      const {data,error}=await rpcMyFailedOfferCounts(); // MSG-RPC-6 : via passerelle (single-shot)
      if(error){console.error("[LANAC] my_failed_offer_counts error:",error.message,error.code);return;}
      if(cancelled) return;
      const map={};
      (Array.isArray(data)?data:[]).forEach(r=>{if(r&&r.listing_id!=null)map[String(r.listing_id)]=Number(r.cnt||0);});
      setOfferFailCounts(map);
    };
    // ── P5-c · COMPTEURS D'INBOX SERVEUR — dormant tant que lanac:inboxPaging != "1" ──
    //  UN agrégat (auth.uid() seul, RPC inbox_counters) qui remplace les balayages
    //  client de `convos` ENTIER (badge de non-lus, plafond réservations, quotas
    //  d'annulation) — pré-requis de la pagination. Alimente l'objet de MODULE (lu à la
    //  demande par les gardes impératives) ET l'état React (badge réactif). Hors chemin
    //  de rendu (arrière-plan). Flag OFF ⇒ AUCUN appel, AUCUNE écriture ⇒ 0 egress ajouté,
    //  comportement identique à aujourd'hui. Échec RPC ⇒ objet inchangé ⇒ repli scan local.
    const fetchInboxCounters=async()=>{
      if(!lanacInboxPagingOn()||!client||!me?.id||cancelled) return;
      const {data,error}=await rpcInboxCounters().then(r=>r,error=>({data:null,error})); // MSG-RPC-6 : via passerelle (single-shot)
      if(error){console.error("[LANAC] inbox_counters error:",error.message||error,error.code);return;}
      if(cancelled) return;
      setLanacInboxServerCounters(data);                                        // objet de module (gardes impératives)
      if(setInboxServerCounters) setInboxServerCounters(lanacInboxServerCounters.value); // état React (badge)
      LANAC_DEBUG&&console.info("[LANAC] inbox_counters",lanacInboxServerCounters.value);
    };
    const fetchUnreadCountsForConvos=async ids=>{
      const list=(ids||[]).filter(Boolean);
      if(!list.length) return;
      const counts=new Map(list.map(id=>[id,0]));
      //  (lot D · D5-c, 30/10/2026) le WATERMARK, toujours — le scan d'avant (`messages.select(…).eq("read",false)`, paginé) est RETIRÉ : il ne tournait
      //   que sur un appareil sans miroir des drapeaux et lisait une colonne que plus rien n'écrit (des comptes gonflés, gardés ensuite par le max()).
      {
        // M5b : compteurs de non-lu par WATERMARK en UN appel (résultat = {convo_id,unread} des fils non-lus),
        // au lieu de télécharger CHAQUE ligne de message non-lu (scan paginé) → egress en baisse. Filtré à `list`.
        // NB : le builder .rpc() est THENABLE mais N'A PAS .catch (≠ builder de requête) → on utilise
        // .then(ok, err) pour capturer un rejet réseau ; une erreur SQL revient déjà dans {data,error}.
        const {data,error}=await rpcMyUnreadCounts().then(r=>r,error=>({data:null,error})); // MSG-RPC-6 : via passerelle (single-shot)
        if(error){console.error("[LANAC] my_unread_counts error:",error.message||error,error.code);return;}
        if(cancelled) return;
        const listSet=new Set(list.map(String));
        (Array.isArray(data)?data:[]).forEach(r=>{if(r&&r.convo_id!=null&&listSet.has(String(r.convo_id)))counts.set(r.convo_id,Number(r.unread||0));});
        applyUnreadCounts(counts);
      }
    };
    // Colonnes fetchConvo : ref_photo exclue (déjà absente de l'inbox via INBOX_CONVO_SELECT) — évite 2 MB d'egress par fetch
    //  (lot D · D6-b, 31/10/2026) sans `read` pour un membre (le filigrane dit le lu) ; l'ADMINISTRATION le garde (pas de filigrane, revue m5).
    const FETCH_CONVO_MSG_SELECT="id,participants,ref_id,ref_title,ref_cat,ref_seller_name,blocked_by,created_at,updated_at,messages(id,convo_id,from_id,text,ts,seq,"+(me.role==="Admin"?"read,":"")+"kind,status,data,image,reply_to,system)"; // M3d : seq requis (aperçu = dernier par seq)
    const fetchConvo=(cid,full=false)=>{
      if(!cid||pendingFetches.has(cid)) return;
      pendingFetches.add(cid);
      let query=client.from("conversations").select(FETCH_CONVO_MSG_SELECT).eq("id",cid);
      if(!full) query=query.order("ts",{foreignTable:"messages",ascending:false}).limit(INBOX_RECENT_MESSAGE_LIMIT,{foreignTable:"messages"});
      query.single()
        .then(({data,error})=>{
          if(error)console.error("[LANAC] conversation fetch error:",error.message,error.code);
          else if(data){
            const next=convoFromDB(data);
            if(next) mergeConvoModels([{...next,messagesLoaded:full}]);
          }
        })
        .catch(error=>console.error("[LANAC] conversation fetch exception:",error?.message||error))
        .finally(()=>pendingFetches.delete(cid));
    };
    //  (lot D · D6-b, 31/10/2026, revue AVANT m6) la branche « avec messages » (`*, messages(*)`, jamais appelée) RETIRÉE.
    const buildConvoQuery=()=>{
      const select=INBOX_CONVO_SELECT;
      const orderAscending=false;
      // ── EGRESS B2 (01/08) · LA VUE ADMIN ÉTAIT LA DERNIÈRE REQUÊTE O(PLATEFORME) ──
      //  Elle tirait TOUTES les conversations, sans borne — linéaire avec la taille totale.
      //  Bornée aux 300 plus RÉCEMMENT ACTIVES.
      //  ⚠️⚠️ DEUX CORRECTIONS DU 02/08 (audit independant), et la premiere etait un VRAI
      //  DEFAUT : je bornais sur `created_at`, donc sur la CREATION. Un litige ouvert il y a un
      //  an mais actif aujourd'hui — le cas type de la moderation — tombait hors des 300 des
      //  qu'il existait 300 conversations plus jeunes. On borne desormais sur `updated_at`
      //  (NOT NULL, bouge a toute activite) : la liste admin garde ce qui VIT.
      //  ⚠️ Et mon commentaire d'origine etait FAUX : « les flux de moderation ouvrent par ID »
      //  — le deep-link exigeait que la conversation soit DEJA dans la liste chargee
      //  (11-messenger : `convos.some(c=>c.id===initConvoId)`), sans repli. Une borne creait
      //  donc un silence. Le repli par ID existe maintenant (fetchConvoRef, cf. plus bas) :
      //  meme famille que le correctif `openListingFromMessage` de la phase 3a.
      const orderCol=me.role==="Admin"?"updated_at":"created_at";
      let query=me.role==="Admin"
        ?client.from("conversations").select(select).order(orderCol,{ascending:orderAscending}).limit(300)
        :client.from("conversations").select(select).contains("participants",[me.id]).order(orderCol,{ascending:orderAscending});
      return query;
    };
    const fetchInboxConversations=async()=>{
      if(me.role==="Admin") return buildConvoQuery();
      const idsStartedAt=performance.now();
      const idsRes=await client.from("conversation_participants").select("convo_id,read_seq").eq("user_id",me.id);
      LANAC_DEBUG&&console.info("[LANAC] loadConvos participant ids raw",{meId:me.id,role:me.role,elapsedMs:Math.round(performance.now()-idsStartedAt),status:idsRes.status,statusText:idsRes.statusText,rows:Array.isArray(idsRes.data)?idsRes.data.length:null,error:idsRes.error?{message:idsRes.error.message,code:idsRes.error.code,details:idsRes.error.details,hint:idsRes.error.hint}:null});
      if(idsRes.error) return {...idsRes,data:null};
      // M5b : peuple le watermark {convoId -> MON read_seq} au même appel (aucun egress ajouté). Dormant tant
      // que pg_read_watermark=OFF (personne ne lit lanacReadSeqMap). Source de vérité de MON read_seq côté client.
      (idsRes.data||[]).forEach(r=>{if(r&&r.convo_id!=null)lanacReadSeqMap.set(r.convo_id,Number(r.read_seq||0));});
      // ── P5-a · drapeaux d'archivage/masquage — état de COMPTE ────────────────────
      //  ⚠️ MESURÉ, puis CORRIGÉ : les lire dans la requête ci-dessus (`select` élargi)
      //  coûtait +37 o par CONVERSATION — PostgREST sérialise les `null`. Soit +5 116 o
      //  au boot pour 138 conversations, et une croissance LINÉAIRE (~18 Ko à 500).
      //  C'est exactement le motif que ce chantier combat.
      //  ⇒ Requête SÉPARÉE et FILTRÉE : elle ne renvoie QUE les lignes réellement
      //  marquées. Pour l'immense majorité (aucune archive) : ZÉRO ligne, ~50 o fixes,
      //  et le coût ne dépend plus du nombre de conversations mais du nombre d'ARCHIVES.
      //  Lancée en parallèle du reste de l'inbox ⇒ hors du chemin de rendu.
      const _flagIds=new Set(); // conversations où je suis participant (garde de migration)
      (idsRes.data||[]).forEach(r=>{if(r&&r.convo_id!=null)_flagIds.add(String(r.convo_id));});
      lanacConvoFlagsMap.clear(); // absence dans la carte = AUCUN drapeau (sémantique du filtre)
      try{
        const _fl=await client.from("conversation_participants")
          .select("convo_id,archived_at,deleted_at").eq("user_id",me.id)
          .or("archived_at.not.is.null,deleted_at.not.is.null");
        if(!_fl.error) (_fl.data||[]).forEach(r=>{if(r&&r.convo_id!=null)
          lanacConvoFlagsMap.set(r.convo_id,{archivedAt:r.archived_at||null,deletedAt:r.deleted_at||null});});
      }catch(e){LANAC_DEBUG&&console.info("[LANAC] P5-a drapeaux ignorés",e&&e.message);}
      // ── MIGRATION DOUCE, UNE SEULE FOIS, IDEMPOTENTE ────────────────────────────
      //  Les archives existantes vivent dans `lanac:conversationUi:<uid>` (appareil).
      //  On les POUSSE vers le serveur, puis on pose un marqueur : personne ne perd ses
      //  archives, et elles suivent enfin d'un appareil à l'autre.
      //  ⭐ On ne pousse QUE ce que le serveur ignore (diff) ⇒ coût nul au régime établi.
      //  Best-effort : un échec laisse le marqueur ABSENT ⇒ nouvelle tentative au
      //  prochain boot. Le localStorage n'est JAMAIS effacé (filet de sécurité).
      try{
        const _migKey=`lanac:convoFlagsMigrated:${me.id}`;
        if(!localStorage.getItem(_migKey)){
          const _ui=JSON.parse(localStorage.getItem(`lanac:conversationUi:${me.id}`)||"{}");
          const _aLocal=Object.keys(_ui.archived||{}), _dLocal=Object.keys(_ui.deleted||{});
          const _todo=[...new Set([..._aLocal,..._dLocal])].filter(cid=>{
            if(!_flagIds.has(String(cid))) return false; // pas (ou plus) participant → on ignore
            const srv=lanacConvoFlagsMap.get(cid)||{archivedAt:null,deletedAt:null}; // absent = aucun drapeau
            return (_aLocal.includes(cid)&&!srv.archivedAt)||(_dLocal.includes(cid)&&!srv.deletedAt);
          });
          // MSG-RPC-1 : via la PASSERELLE (nom RPC + params centralisés ; single-shot). `?true:undefined` produit le
          //  MÊME p_* que l'ancien `?true:null` (undefined⇒null dans la passerelle). `.then(r,e)` inchangé (rejets propagés).
          const _res=await Promise.all(_todo.map(cid=>rpcSetConversationFlags(cid,{archived:_aLocal.includes(cid)?true:undefined,deleted:_dLocal.includes(cid)?true:undefined}).then(r=>r,e=>({error:e}))));
          if(!_res.some(r=>r&&r.error)){
            _todo.forEach(cid=>{const f=lanacConvoFlagsMap.get(cid)||{archivedAt:null,deletedAt:null};
              lanacConvoFlagsMap.set(cid,{archivedAt:_aLocal.includes(cid)?new Date().toISOString():f.archivedAt,
                                          deletedAt:_dLocal.includes(cid)?new Date().toISOString():f.deletedAt});});
            localStorage.setItem(_migKey,"1");
            LANAC_DEBUG&&console.info("[LANAC] P5-a migration drapeaux convo",{pousses:_todo.length});
          }
        }
      }catch(e){LANAC_DEBUG&&console.info("[LANAC] P5-a migration ignorée",e&&e.message);}
      const ids=[...new Set((idsRes.data||[]).map(r=>r.convo_id).filter(Boolean))];
      if(!ids.length) return {data:[],error:null,count:null,status:200,statusText:"OK"};
      // ── P5-d · PAGINATION KEYSET (dormant, lanac:inboxPaging) ───────────────────
      //  Remplace le fetch de TOUTES les coquilles par la PAGE 1 (30) de `inbox_page`,
      //  les suivantes arrivant à la demande (sentinelle de ConversationList).
      //  ⭐ CONTRAT « la pagination gouverne ce qu'on RÉCUPÈRE, jamais ce qu'on AFFICHE » :
      //  l'hydratation du cache (plus haut) a déjà rendu ce qu'on possède ; les pages ne font
      //  que COMPLÉTER par mergeConvoModels. L'ordre d'affichage reste celui du client.
      //  ⭐ AUCUN saut/doublon possible : le keyset serveur énumère sur une clé STABLE et
      //  MONOTONE (last_message_at, trigger AFTER INSERT, jamais rétrogradé + id). Audité
      //  27/07 sur les 138 convos de recette : clé serveur == max(ts) client à la seconde
      //  près (0 divergence >2 s) ; seules les convos SANS message divergent (dernières côté
      //  client, à leur création côté serveur) — un rang d'apparition, pas une perte.
      //  ⚠️ Les requêtes AMONT (ids+read_seq, drapeaux) restent : le watermark M5b et les
      //  drapeaux P5-a en dépendent, et ce sont des projections légères.
      //  ⚠️ Le shell-delta (MSG-P1) est COURT-CIRCUITÉ drapeau armé — ils répondent au même
      //  problème ; le cache de coquilles n'est NI lu NI écrit ici (un cache partiel ferait
      //  échouer sa garantie de couverture au retour drapeau OFF ⇒ repli full sain).
      //  Repli INFAILLIBLE : erreur RPC ⇒ on continue sur le chemin historique ci-dessous.
      if(lanacInboxPagingOn()&&me.role!=="Admin"){
        const PAGE=30;
        const pageRes=await rpcInboxPage(PAGE).then(r=>r,error=>({data:null,error}));
        if(pageRes.error||!Array.isArray(pageRes.data)){
          console.error("[LANAC] inbox_page error (repli chemin historique):",pageRes.error?.message||pageRes.error,pageRes.error?.code);
        }else{
          const page1=pageRes.data;
          let cursor=page1.length?{activity:page1[page1.length-1].activity_key,id:page1[page1.length-1].id}:null;
          let hasMore=page1.length===PAGE;
          let enVol=false; // une seule page en vol : l'observer peut tirer plusieurs fois
          // ── ARCHIVES (29/08) · sous-pageur PARESSEUX ────────────────────────────────
          //  `inbox_page(p_archived=false)` exclut les archivées ⇒ pagination armée, leurs
          //  coquilles n'arrivaient par AUCUN chemin : la vue archives ne montrait que ce
          //  que le hasard (cache, realtime) avait hydraté. Ce sous-pageur les rapatrie —
          //  UNIQUEMENT quand la vue archives s'ouvre (zéro egress sinon), par pages keyset
          //  bornées, via le MÊME pipeline additif (mergeConvoModels + previews) : jamais
          //  d'écrasement, l'ordre d'affichage reste au client. `archivedAt` vient de
          //  lanacConvoFlagsMap (P5-a, déjà chargée au boot) via convoFromDB.
          let archCursor=null, archHasMore=true, archEnVol=false, archPrimed=false;
          lanacInboxPager.value={
            get hasMore(){return hasMore;},
            get archivedHasMore(){return archPrimed&&archHasMore;},
            loadArchived:async()=>{
              if(archEnVol||cancelled||(archPrimed&&!archHasMore)) return;
              archEnVol=true;
              try{
                const res=await rpcInboxPage(PAGE,archCursor?.activity,archCursor?.id,true).then(r=>r,error=>({data:null,error}));
                if(cancelled) return;
                if(res.error||!Array.isArray(res.data)){console.error("[LANAC] inbox_page archives error:",res.error?.message||res.error,res.error?.code);return;}
                archPrimed=true;
                const rows=res.data;
                if(rows.length){
                  archCursor={activity:rows[rows.length-1].activity_key,id:rows[rows.length-1].id};
                  mergeConvoModels(rows.map(convoFromDB).filter(Boolean),"inbox-page-archives");
                  hydrateInboxPreviews(rows).catch(error=>console.error("[LANAC] inbox archives preview hydrate error:",error?.message||error));
                }
                archHasMore=rows.length===PAGE;
                LANAC_DEBUG&&console.info("[LANAC] inbox_page archives",{recues:rows.length,archHasMore});
              }finally{archEnVol=false;}
            },
            loadMore:async()=>{
              if(!hasMore||enVol||cancelled) return;
              enVol=true;
              try{
                const res=await rpcInboxPage(PAGE,cursor?.activity,cursor?.id).then(r=>r,error=>({data:null,error}));
                if(cancelled) return;
                if(res.error||!Array.isArray(res.data)){console.error("[LANAC] inbox_page loadMore error:",res.error?.message||res.error,res.error?.code);return;}
                const rows=res.data;
                if(rows.length){
                  cursor={activity:rows[rows.length-1].activity_key,id:rows[rows.length-1].id};
                  mergeConvoModels(rows.map(convoFromDB).filter(Boolean),"inbox-page");
                  hydrateInboxPreviews(rows).catch(error=>console.error("[LANAC] inbox page preview hydrate error:",error?.message||error));
                }
                hasMore=rows.length===PAGE;
                LANAC_DEBUG&&console.info("[LANAC] inbox_page loadMore",{recues:rows.length,hasMore});
              }finally{enVol=false;}
            },
          };
          LANAC_DEBUG&&console.info("[LANAC] inbox_page page 1",{recues:page1.length,total:ids.length,hasMore});
          return {data:page1,error:null,count:null,status:200,statusText:"OK"};
        }
      }
      // MSG-P1 (sync-core, ARMÉ par défaut — kill-switch lanac:inboxShellDelta="0") : delta des COQUILLES par
      //  updated_at. conversations.updated_at = horloge murale (PROUVÉ : bump sur chaque message, 209/209 convos
      //  ⇒ updated_at ≥ ts du dernier message, 0 en retard) → tout changement postérieur au dernier sync a un
      //  timestamp > cursor (=max updated_at caché) ⇒ `.gt(cursor)` les capte TOUS ; les inchangées viennent du
      //  cache. Le delta n'ajoute AUCUNE requête (juste un filtre sur la requête coquilles existante) et est ⊆
      //  full ⇒ l'egress ne peut JAMAIS augmenter. Repli INFAILLIBLE : cache absent/incomplet/erreur ⇒ full
      //  historique. Le résultat garde la MÊME forme (lignes DB snake_case + updated_at) ⇒ convoFromDB /
      //  hydrateInboxPreviews inchangés (le delta des previews reste correct, chaque coquille portant son
      //  updated_at). Recette live paul/jean (e2e S38 + full armé) OK ; "0" pour désarmer sans redéploiement.
      //  ⚠️ INVARIANT : tout champ de coquille (INBOX_CONVO_SELECT) qui change DOIT bumper updated_at. Audité :
      //   messages ⇒ trigger on_message_inserted bumpe (209/209). `blocked_by` : serveur-autoritatif depuis C7 ·
      //   bloqueurs (15/09) — il ne change que par set_conversation_block, et la garde guard_conversation_immutable_fields
      //   avance updated_at à chaque blocage/déblocage (la commande aussi) ⇒ le delta sert le blocked_by de la base.
      // C7 · bloqueurs (15/09) : le curseur garde la PRÉCISION de la base (microsecondes). `Date.parse` la tronquait à la
      //  milliseconde : `.gt(curseur tronqué)` relisait, à chaque démarrage chaud, toutes les coquilles de la date la plus
      //  récente — une d'ordinaire (371 o, les « 0,7 % » de S38), mais TOUTES celles qu'une même transaction avance ensemble :
      //  un blocage avance tous les fils de la paire au même instant (36 chez le compte de recette, 13,9 Ko relus à chaque
      //  démarrage). Le curseur part en UTC « Z », six décimales : les mêmes caractères qu'avant dans l'URL. Format
      //  inattendu ⇒ repli sur l'ancien calcul (milliseconde), jamais sur « rien ».
      const microsDe=v=>{const m=/^(\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d)(?:\.(\d{1,6}))?(Z|[+-]\d\d:?\d\d)$/.exec(String(v||""));if(!m) return null;const base=Date.parse(m[1]+m[3]);return Number.isFinite(base)?base*1000+Number((m[2]||"").padEnd(6,"0")):null;};
      const isoMicros=u=>new Date(Math.floor(u/1e6)*1000).toISOString().slice(0,19)+"."+String(u%1e6).padStart(6,"0")+"Z";
      const shellDeltaOn=(()=>{try{return localStorage.getItem("lanac:inboxShellDelta")!=="0";}catch(e){return true;}})();
      let cachedShellById=null,cursorIso=null;
      if(shellDeltaOn){
        try{
          const raw=localStorage.getItem(CACHE_CONVOSHELLS_KEY(me.id));
          const arr=raw?JSON.parse(raw):null;
          if(Array.isArray(arr)&&arr.length){
            const m=new Map();let cur=null,curIso=null;
            arr.forEach(s=>{if(s&&s.id!=null){m.set(String(s.id),s);
              const micro=microsDe(s.updated_at),ms=Date.parse(s.updated_at||"");
              const u=micro!=null?micro:(Number.isFinite(ms)?ms*1000:null);
              if(u!=null&&(cur==null||u>cur)){cur=u;curIso=micro!=null?isoMicros(micro):new Date(ms).toISOString();}}});
            // Cache exploitable SSI il couvre TOUTES les convos courantes (sinon une inchangée hors-cache serait
            //  perdue) ET a un curseur valide → sinon repli full (1er sync, nouvel appareil, convo neuve non cachée).
            if(curIso&&ids.every(id=>m.has(String(id)))){cachedShellById=m;cursorIso=curIso;}
          }
        }catch(e){cachedShellById=null;cursorIso=null;}
      }
      const rows=[];
      let deltaOk=false;
      if(cachedShellById&&cursorIso){
        const changed=new Map();
        deltaOk=true;
        for(let i=0;i<ids.length;i+=INBOX_CONVO_ID_CHUNK_SIZE){
          if(cancelled){deltaOk=false;break;}
          const chunk=ids.slice(i,i+INBOX_CONVO_ID_CHUNK_SIZE);
          const res=await client.from("conversations").select(INBOX_CONVO_SELECT).in("id",chunk).gt("updated_at",cursorIso).order("created_at",{ascending:false});
          if(res.error){LANAC_DEBUG&&console.info("[LANAC] loadConvos shell-delta chunk erreur → repli full",{code:res.error.code});deltaOk=false;break;}
          if(Array.isArray(res.data)) res.data.forEach(r=>{if(r&&r.id!=null)changed.set(String(r.id),r);});
        }
        if(deltaOk){
          ids.forEach(id=>{const k=String(id);const s=changed.get(k)||cachedShellById.get(k);if(s)rows.push(s);});
          LANAC_DEBUG&&console.info("[LANAC] loadConvos shell-delta OK",{convos:ids.length,changed:changed.size,cursor:cursorIso});
        }
      }
      if(!deltaOk){ // FULL (comportement historique) — repli infaillible + 1er sync + delta désarmé
        rows.length=0;
        for(let i=0;i<ids.length;i+=INBOX_CONVO_ID_CHUNK_SIZE){
          if(cancelled) break;
          const chunk=ids.slice(i,i+INBOX_CONVO_ID_CHUNK_SIZE);
          const chunkStartedAt=performance.now();
          const chunkRes=await client.from("conversations").select(INBOX_CONVO_SELECT).in("id",chunk).order("created_at",{ascending:false});
          const chunkElapsedMs=Math.round(performance.now()-chunkStartedAt);
          LANAC_DEBUG&&console.info("[LANAC] loadConvos conversations chunk raw",{meId:me.id,role:me.role,chunkIndex:Math.floor(i/INBOX_CONVO_ID_CHUNK_SIZE)+1,chunkSize:chunk.length,elapsedMs:chunkElapsedMs,status:chunkRes.status,statusText:chunkRes.statusText,rows:Array.isArray(chunkRes.data)?chunkRes.data.length:null,error:chunkRes.error?{message:chunkRes.error.message,code:chunkRes.error.code,details:chunkRes.error.details,hint:chunkRes.error.hint}:null});
          if(chunkRes.error) return {...chunkRes,data:null};
          if(Array.isArray(chunkRes.data)) rows.push(...chunkRes.data);
        }
      }
      rows.sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
      // Persiste le cache LÉGER des coquilles (toutes convos, projection snake_case = INBOX_CONVO_SELECT) pour le
      //  prochain delta. Écrit seulement si armé (dormant strict : flag OFF ⇒ 0 écriture, comportement inchangé).
      if(shellDeltaOn){try{localStorage.setItem(CACHE_CONVOSHELLS_KEY(me.id),JSON.stringify(rows.map(r=>({id:r.id,participants:r.participants,ref_id:r.ref_id,ref_title:r.ref_title,ref_cat:r.ref_cat,created_at:r.created_at,updated_at:r.updated_at,ref_seller_name:r.ref_seller_name,blocked_by:r.blocked_by}))));}catch(e){}}
      return {data:rows,error:null,count:null,status:200,statusText:"OK"};
    };
    // Hydratation DELTA des aperçus inbox (2026-07-10). Problème : le boot ne charge aucun message (choix
    // perf/egress de juin) → une conversation absente du cache local (nouvel appareil, ou nouvelle conversation
    // reçue hors-ligne) s'affichait en « ligne fantôme » : avatar + badge, sans titre/aperçu/heure, mal triée.
    // Design : UNE passe bornée après le fetch des coquilles — seules les conversations SANS trace locale ou dont
    // `updated_at` serveur dépasse le dernier message connu (marge 60 s d'horloge) sont hydratées, à raison de
    // 1 SEUL message par conversation (limit par parent, parent réduit à `id`). Fusion via mergeMessages
    // (additive, par id, ne touche NI messagesLoaded — l'ouverture refait le fetch complet — NI unreadCount).
    // Boot à cache chaud = 0 requête. Appelée UNIQUEMENT par loadConvos (boot + pull-to-refresh) : aucun effet
    // réarmable, aucun polling — borné par construction (cf tempêtes d'egress, plan Supabase payant).
    const hydrateInboxPreviews=async rawRows=>{
      try{
        // 0) Restauration LOCALE (0 réseau) depuis le side-car d'aperçus : les conversations au-delà des 30 du
        //    cache principal retrouvent leur dernier message (aperçu + heure + tri) sans rien télécharger.
        let sidecar={};
        try{const rawPv=typeof localStorage!=="undefined"&&localStorage.getItem(CACHE_PREVIEWS_KEY(me.id));if(rawPv)sidecar=JSON.parse(rawPv)||{};}catch(e){sidecar={};}
        const local=new Map((convosRef.current||[]).map(c=>[c.id,c]));
        const rowIds=new Set((rawRows||[]).map(r=>r?.id).filter(Boolean));
        const seed=new Map();
        rowIds.forEach(id=>{
          const sm=sidecar[id];
          if(!sm||!sm.id||!sm.ts) return;
          const lc=local.get(id);
          const lastTs=lc?convoActivityTs(lc):0; // M3d : fraîcheur = max ts (sinon un dernier-par-seq à ts ancien sous-estimerait le connu)
          if(Number(sm.ts||0)>lastTs) seed.set(id,sm);
        });
        if(seed.size&&!cancelled) setConvos(p=>p.map(c=>{
          const sm=seed.get(c.id);
          if(!sm) return c;
          return normalizeConvoForInbox(c,me.id,{messages:mergeConvoMessageLists(c.messages||[],[sm])});
        }));
        // 1) Delta réseau : uniquement les conversations SANS aucun état connu (local + side-car) ou dont
        //    updated_at serveur dépasse ce qu'on connaît (marge 60 s d'horloge).
        // ── EGRESS B0 (01/08/2026) · LE DELTA SE CURSEURE DÉSORMAIS SUR SA PROPRE HORLOGE ──
        //  ⚠️⚠️ LE DÉFAUT MESURÉ : ce filtre comparait `updated_at` (bougé par TOUT changement —
        //  lecture, drapeau, deal) au ts du DERNIER MESSAGE. Toute conversation dont updated_at
        //  avait été poussé par autre chose qu'un message devenait périmée POUR TOUJOURS :
        //  re-téléchargée à chaque boot sans que le re-téléchargement change rien (l'aperçu
        //  revient identique, updated_at reste devant). Mesuré en prod : 43 convos/139 dans cet
        //  état ⇒ ~36,6 Ko de poids mort à CHAQUE boot chaud, pour chaque utilisateur.
        //  ⭐ Le curseur `previewsync` mémorise le updated_at VU à la dernière synchro RÉUSSIE :
        //  `upd ≤ vu` ⇒ rien de nouveau depuis qu'on a TOUT lu jusqu'à `vu` ⇒ pas de réseau.
        //  Un bump non-message re-télécharge UNE fois (upd > vu), enregistre `vu=upd`, se tait.
        //  Un vrai message ultérieur (upd2 > vu) re-déclenche : la correction est préservée.
        //  Écrit APRÈS le fetch réussi seulement — le fetch est la preuve qu'on a vu jusqu'à upd.
        let syncVu={};
        const syncOn=lanacPreviewSyncOn();
        if(syncOn){try{const rawSy=typeof localStorage!=="undefined"&&localStorage.getItem(CACHE_PREVIEWSYNC_KEY(me.id));if(rawSy)syncVu=JSON.parse(rawSy)||{};}catch(e){syncVu={};}}
        const updOf=new Map((rawRows||[]).filter(r=>r?.id).map(r=>[r.id,new Date(r.updated_at||0).getTime()]));
        const knownTs=id=>{
          const lc=local.get(id);
          const a=lc?convoActivityTs(lc):0; // M3d : fraîcheur = max ts (hors drapeau seq)
          const b=Number(sidecar[id]?.ts||0);
          return Math.max(a,b);
        };
        const staleIds=(rawRows||[]).filter(r=>{
          if(!r?.id) return false;
          const upd=updOf.get(r.id);
          const t=knownTs(r.id);
          // ⚠️⚠️ ORDRE CORRIGE LE 02/08 (audit independant) — LE CURSEUR PASSAIT AVANT CE TEST,
          //  ET C'ETAIT UN DEFAUT PERMANENT. Le curseur dit « j'ai tout vu jusqu'a `upd` » ; il ne dit
          //  PAS « et je l'ai encore ». Or les deux ecrivains de l'etat local filtrent sur la
          //  PARTICIPATION (useMessagingState) tandis que le curseur, lui, etait ecrit pour TOUTES
          //  les lignes hydratees. Consequence mesurable : un ADMIN (300 conversations dont il n'est
          //  pas participant) ecrivait 300 curseurs, ne persistait AUCUN apercu, et au boot suivant
          //  n'hydratait plus RIEN — lignes sans apercu ni heure, triees en fin de liste, SANS
          //  REPARATION POSSIBLE. Meme effet pour un compte ordinaire si le side-car d'apercus est
          //  purge par quota (son `catch` l'efface sans toucher au curseur).
          //  ⭐ REGLE : un cache de SYNCHRO ne peut jamais autoriser a sauter une lecture quand le
          //  cache de DONNEES qu'il accompagne est absent. L'absence d'etat connu prime sur tout.
          if(!t) return true;
          if(syncOn&&Number.isFinite(upd)&&upd>0&&upd<=Number(syncVu[r.id]||0)) return false; // etat connu ET rien de neuf depuis
          return Number.isFinite(upd)&&upd>t+60000;
        }).map(r=>r.id);
        LANAC_DEBUG&&console.info("[LANAC] hydrateInboxPreviews",{total:(rawRows||[]).length,stale:staleIds.length});
        if(!staleIds.length||cancelled) return;
        const syncAcquis=[];
        for(let i=0;i<staleIds.length&&!cancelled;i+=INBOX_CONVO_ID_CHUNK_SIZE){
          const chunk=staleIds.slice(i,i+INBOX_CONVO_ID_CHUNK_SIZE);
          const res=await client.from("conversations").select(me.role==="Admin"?INBOX_PREVIEW_SELECT_ADMIN:INBOX_PREVIEW_SELECT).in("id",chunk).order("ts",{foreignTable:"messages",ascending:false}).limit(1,{foreignTable:"messages"});
          if(res.error){console.error("[LANAC] inbox preview hydrate error:",res.error.message,res.error.code);break;}
          mergeMessages((res.data||[]).flatMap(r=>Array.isArray(r?.messages)?r.messages:[]));
          if(syncOn) (res.data||[]).forEach(r=>{if(r?.id)syncAcquis.push(r.id);}); // synchro RÉUSSIE pour ces ids
        }
        if(syncOn&&syncAcquis.length&&!cancelled){
          try{
            syncAcquis.forEach(id=>{const u=updOf.get(id);if(Number.isFinite(u)&&u>0)syncVu[id]=u;});
            // Borné comme le side-car d'aperçus : on garde les 400 curseurs les plus récents.
            const entries=Object.entries(syncVu).sort((a,b)=>Number(b[1])-Number(a[1])).slice(0,400);
            localStorage.setItem(CACHE_PREVIEWSYNC_KEY(me.id),JSON.stringify(Object.fromEntries(entries)));
          }catch(e){try{localStorage.removeItem(CACHE_PREVIEWSYNC_KEY(me.id));}catch(e2){}}
        }
      }catch(e){console.error("[LANAC] inbox preview hydrate exception:",e?.message||e);}
    };
    const loadConvos=async()=>{
      lastConvosLoadAt=Date.now(); // Fix D : arme la fenêtre de throttle (couvre boot + pull-to-refresh + reconnexion)
      const loadStartedAt=performance.now();
      const mapRows=(source,data,count,status,statusText,elapsedMs)=>{
        const rawRows=Array.isArray(data)?data.length:null;
        const mapped=(Array.isArray(data)?data:[]).map(convoFromDB).filter(Boolean);
        const visible=mapped.filter(canSeeConvo);
        const rows=visible.map(c=>normalizeConvoForInbox(c,me.id));
        LANAC_DEBUG&&console.info("[LANAC] loadConvos rows after convoFromDB",{source,rawRows,mappedRows:mapped.length,droppedByMapper:(rawRows??0)-mapped.length,count,status,statusText,elapsedMs,sampleIds:mapped.slice(0,5).map(c=>c.id)});
        LANAC_DEBUG&&console.info("[LANAC] loadConvos rows after canSeeConvo",{source,mappedRows:mapped.length,visibleRows:visible.length,droppedByCanSee:mapped.length-visible.length,meId:me.id,role:me.role,sampleVisibleIds:visible.slice(0,5).map(c=>c.id)});
        if(!rows.length) console.warn(`[LANAC] loadConvos: 0 conversations pour me.id = ${me.id}, role = ${me.role}`,{source,rawRows,count,status,statusText,elapsedMs,data});
        return rows;
      };
      try{
        LANAC_DEBUG&&console.info("[LANAC] loadConvos start",{meId:me.id,role:me.role,at:new Date().toISOString()});

        const simpleStartedAt=performance.now();
        const simpleRes=await fetchInboxConversations();
        const simpleElapsedMs=Math.round(performance.now()-simpleStartedAt);
        LANAC_DEBUG&&console.info("[LANAC] loadConvos simple raw conversations",{meId:me.id,role:me.role,elapsedMs:simpleElapsedMs,count:simpleRes.count,status:simpleRes.status,statusText:simpleRes.statusText,rows:Array.isArray(simpleRes.data)?simpleRes.data.length:null,error:simpleRes.error?{message:simpleRes.error.message,code:simpleRes.error.code,details:simpleRes.error.details,hint:simpleRes.error.hint}:null,data:simpleRes.data});
        if(simpleRes.error){console.error("[LANAC] conversations simple fetch error:",simpleRes.error.message,simpleRes.error.code,{meId:me.id,role:me.role,count:simpleRes.count,status:simpleRes.status,statusText:simpleRes.statusText,elapsedMs:simpleElapsedMs});return;}
        if(!Array.isArray(simpleRes.data)||cancelled){
          console.warn("[LANAC] loadConvos: simple conversations response is not an array",{meId:me.id,role:me.role,count:simpleRes.count,status:simpleRes.status,statusText:simpleRes.statusText,elapsedMs:simpleElapsedMs,data:simpleRes.data});
          return;
        }
        const simpleRows=mapRows("simple",simpleRes.data,simpleRes.count,simpleRes.status,simpleRes.statusText,simpleElapsedMs);
        LANAC_DEBUG&&console.info("[LANAC] loadConvos mergeConvoModels(simple)",{rows:simpleRows.length});
        mergeConvoModels(simpleRows,"loadConvos-simple");
        LANAC_DEBUG&&console.info("[LANAC] loadConvos setConvosLoading(false) after simple",{rows:simpleRows.length});
        if(!cancelled) setConvosLoading(false);
        fetchUnreadCountsForConvos(simpleRows.map(c=>c.id)).catch(error=>console.error("[LANAC] unread count background error:",error?.message||error,{meId:me.id,role:me.role}));
        // Aperçus manquants/périmés : delta borné sur les lignes BRUTES (elles portent updated_at), en arrière-plan.
        hydrateInboxPreviews(simpleRes.data).catch(error=>console.error("[LANAC] inbox preview hydrate background error:",error?.message||error));
        // Lot 7 (Phase 3b) : snapshot du plafond d'offres — 1 agrégat borné (my_failed_offer_counts, RLS-scopé),
        // renvoyé VIDE pour qui n'a jamais fait d'offre ratée → coût egress quasi nul. Ferme le trou du pré-contrôle.
        fetchMyFailedOfferCounts().catch(error=>console.error("[LANAC] offer fail counts background error:",error?.message||error));
        // P5-c : agrégats serveur de l'inbox (dormant tant que lanac:inboxPaging != "1"). Pré-requis de la pagination.
        fetchInboxCounters().catch(error=>console.error("[LANAC] inbox counters background error:",error?.message||error));

        LANAC_DEBUG&&console.info("[LANAC] loadConvos embedded skipped",{meId:me.id,role:me.role,reason:"avoid conversations participants/messages embedded timeout; messages load on conversation open"});
      }catch(error){
        if(!cancelled) console.error("[LANAC] conversations load error:",error?.message||error);
      }finally{
        LANAC_DEBUG&&console.info("[LANAC] loadConvos end",{meId:me.id,role:me.role,elapsedMs:Math.round(performance.now()-loadStartedAt),cancelled});
        if(!cancelled){LANAC_DEBUG&&console.info("[LANAC] loadConvos setConvosLoading(false) finally",{meId:me.id,role:me.role});setConvosLoading(false);}
      }
    };
    loadConvosRef.current=loadConvos; // Pull-to-refresh inbox : expose le dernier loadConvos (sans refactor)
    // 02/08 · REPLI PAR ID (audit) : une conversation hors de la liste chargee (borne admin 300,
    //  page 1 paginee) doit rester ATTEIGNABLE par deep-link. Meme idiome que loadConvosRef.
    if(ports.fetchConvoRef) ports.fetchConvoRef.current=fetchConvo;
    //  Audit 23/09 : la relecture des compteurs, pour le fil qui vient d'être lu (useConversationChannel). Même garde que
    //  l'appel du chargement : drapeau de pagination, client, compte — et `cancelled` rend inerte celle d'un compte quitté.
    if(ports.refreshInboxCountersRef) ports.refreshInboxCountersRef.current=fetchInboxCounters;
    loadConvos();
    // Étape 3 — filet de sécurité clôture : un deal confirmé (source serveur) doit se refléter chez l'acheteur
    // (et le vendeur) MÊME si l'UPDATE du message dealConfirm a été manqué (canal tombé, course realtime).
    // On réconcilie depuis la base : (1) l'annonce passe à son statut final ; (2) la conversation est rafraîchie
    // → le message dealConfirm passe "confirmé" et l'invite d'avis apparaît. Aucune écriture, lecture seule bornée.
    const pendingDealReconciliations=new Set();
    const dealConvoMatches=(c,d)=>String(c?.refId)===String(d?.itemId)&&c?.participants?.includes(d?.buyerId)&&c?.participants?.includes(d?.sellerId);
    const dealConversationIsCurrent=(c,d)=>{
      const messages=c?.messages||[];
      const reviewReady=messages.some(m=>m.kind==="dealConfirmed"&&m.data?.dealId===d.id);
      // Un deal visite (visit_only) n'a pas de carte dealConfirm "transaction a confirmer" :
      // la carte d'avis (creee serveur) suffit. Sinon on exige la confirmation du prompt.
      if(d.visitOnly) return reviewReady;
      const promptConfirmed=messages.some(m=>m.kind==="dealConfirm"&&m.data?.dealId===d.id&&m.status==="confirmed");
      return promptConfirmed&&reviewReady;
    };
    const fetchDealConversation=async d=>{
      const local=(convosRef.current||[]).find(c=>dealConvoMatches(c,d));
      if(local){
        if(!dealConversationIsCurrent(local,d)) fetchConvo(local.id);
        return;
      }
      // La conversation peut ne pas encore être dans l'inbox locale au moment du rattrapage.
      // Lecture ciblée d'une seule conversation, sans créer ni modifier aucune donnée.
      await client.from("conversations")
        .select(FETCH_CONVO_MSG_SELECT)
        .eq("ref_id",d.itemId)
        .contains("participants",[d.buyerId,d.sellerId])
        .order("updated_at",{ascending:false})
        .order("ts",{foreignTable:"messages",ascending:false})
        .limit(INBOX_RECENT_MESSAGE_LIMIT,{foreignTable:"messages"})
        .limit(1)
        .maybeSingle()
        .then(({data,error})=>{
          if(cancelled) return;
          if(error){console.error("[LANAC] confirmed deal conversation catch-up error:",error.message,error.code);return;}
          const next=convoFromDB(data);
          if(next) mergeConvoModels([{...next,messagesLoaded:false}],"confirmed-deal-catch-up");
        })
        .catch(error=>console.error("[LANAC] confirmed deal conversation catch-up exception:",error?.message||error));
    };
    // B6 (02/09) · MÊME prédicat pour le chemin unitaire (live) ET le chemin batch (boot) —
    //  une seule définition, jamais deux vérités.
    const dealNeedsListingCatchUp=d=>{
      const itemId=String(d.itemId);
      const loadedItem=(itemsRef.current||[]).find(x=>String(x.id)===itemId);
      const loadedFeed=(feedItemsRef.current||[]).find(x=>String(x.id)===itemId);
      const opened=String(selRef.current?.id||"")===itemId?selRef.current:null;
      return [loadedItem,loadedFeed,opened].filter(Boolean).some(x=>!isClosedListingStatus(x.status));
    };
    const reconcileConfirmedDeal=async d=>{
      if(cancelled||!d||d.status!=="confirmed"||!d.itemId||pendingDealReconciliations.has(d.id)) return;
      pendingDealReconciliations.add(d.id);
      try{
        const itemId=String(d.itemId);
        const needsListingCatchUp=dealNeedsListingCatchUp(d);
        // Ne charge pas une annonce absente des états locaux : cela préserverait mal le contrat de pagination.
        // Si elle est affichée et encore non finale, une seule lecture remet toutes ses copies locales à jour.
        if(needsListingCatchUp){
          const {data}=await client.from("listings").select("*").eq("id",d.itemId).maybeSingle();
          if(!cancelled&&data) applyFreshListingRow(itemId,data);
        }
        if(!cancelled) await fetchDealConversation(d);
      }catch(error){
        console.error("[LANAC] confirmed deal reconciliation exception:",error?.message||error);
      }finally{
        pendingDealReconciliations.delete(d.id);
      }
    };
    // Le MERGE d'une ligne annonce fraîche — extrait pour être partagé par le chemin unitaire
    // (live) et le chemin batch (boot), au caractère près.
    const applyFreshListingRow=(itemId,data)=>{
      const it=listingFromDB(data);
      if(!it) return;
      // Vues MONOTONES : `it.views` vient de listings.views (colonne figée depuis que la source des vues
      // est listing_views — cf. upsert realtime L11263). Cette lecture de réconciliation ne doit jamais
      // faire régresser un compteur déjà hydraté plus frais. On conserve le max par collection (miroir L11264-11270).
      const _mv=(a,b)=>Math.max(Number(a||0),Number(b||0));
      setItems(prev=>prev.map(x=>String(x.id)===itemId?{...x,...it,views:_mv(it.views,x.views)}:x));
      setFeedItems(prev=>prev.map(x=>String(x.id)===itemId?{...it,views:_mv(it.views,x.views)}:x));
      setSel(prev=>prev&&String(prev.id)===itemId?{...prev,...it,views:_mv(it.views,prev.views)}:prev);
    };
    // B6 (02/09) · CHEMIN BATCH du rattrapage (boot/retour au 1er plan) — la sonde pg_stat de
    //  prod a montré le motif N+1 (des dizaines de milliers de `listings select=* eq id` à
    //  1 ligne par appel). Ici : les MÊMES gardes et le MÊME merge que le chemin unitaire,
    //  mais UNE requête `.in()` pour toutes les annonces à rattraper. Le chemin unitaire
    //  (applyDealRow, live) est INCHANGÉ — un deal qui arrive seul se rattrape seul.
    const reconcileConfirmedDealsBatch=async list=>{
      const eligibles=(list||[]).filter(d=>!cancelled&&d&&d.status==="confirmed"&&d.itemId&&!pendingDealReconciliations.has(d.id));
      if(!eligibles.length) return;
      eligibles.forEach(d=>pendingDealReconciliations.add(d.id));
      try{
        const besoins=eligibles.filter(dealNeedsListingCatchUp);
        const ids=[...new Set(besoins.map(d=>Number(d.itemId)).filter(Boolean))];
        if(ids.length){
          const {data}=await client.from("listings").select("*").in("id",ids);
          if(!cancelled&&Array.isArray(data)) data.forEach(row=>applyFreshListingRow(String(row.id),row));
        }
        for(const d of eligibles){ if(cancelled) break; await fetchDealConversation(d); }
      }catch(error){
        console.error("[LANAC] confirmed deals batch reconciliation exception:",error?.message||error);
      }finally{
        eligibles.forEach(d=>pendingDealReconciliations.delete(d.id));
      }
    };
    // Application d'un deal (ligne DB brute) — logique UNIQUE partagée par postgres_changes ET Broadcast :
    // dédup par id (setDeals idempotent : remplace si présent, sinon insère en tête) + réconciliation
    // idempotente (garde pendingDealReconciliations). L'entrée est identique dans les deux voies
    // (payload.new de postgres_changes == msg.payload.record du trigger = to_jsonb(new)).
    const applyDealRow=row=>{
      const d=dealFromDB(row); if(!d) return;
      setDeals(p=>p.some(x=>x.id===d.id)?p.map(x=>x.id===d.id?d:x):[d,...p]);
      reconcileConfirmedDeal(d);
    };
    applyDealRef.current=applyDealRow; // pont vers le canal `lanac:user:<me>` (effet NOTIF)
    // Phase 2c — application d'un message (ligne DB brute) pour la voie INBOX (liste, non-lus, bannière) :
    // logique UNIQUE partagée par postgres_changes ET Broadcast. setConvos dédupe par id (idempotent) ;
    // le delta de non-lus s'auto-annule au 2e passage ; fetchConvo est gardé (pendingFetches). SEUL effet
    // de bord non idempotent = la bannière glass → dédupliquée par id de message (toastedMsgIds), ce qui
    // la rend sûre en double-run ET après le retrait futur de postgres_changes. Le fil OUVERT n'est PAS
    // concerné (il passe par le Broadcast client lanac:convo:<id> du messenger, inchangé).
    const toastedMsgIds=new Set();
    const applyMessageInsert=row=>{
      const msg=messageFromDB(row);
      if(!msg)return;
      const cid=row.convo_id;
      setConvos(p=>{
        if(!p.some(c=>c.id===cid)){fetchConvo(cid);return p;}
        return p.map(c=>{
          if(c.id!==cid) return c;
          const existing=c.messages.find(m=>m.id===msg.id);
          // Déjà présent : no-op SAUF upgrade PREVIEW→FULL (remplace en place, unread inchangé — même message).
          if(existing){
            // 2026-07-18 (audit A1) : écho serveur d'un message déjà posé en OPTIMISTE (le mien, ou une carte
            // transactionnelle insérée par mon client) → on adopte le seq attribué quand la copie locale n'en a
            // pas. pg_seq_ordering trie « seq null EN BAS » : sans cette adoption, la bulle de l'expéditeur
            // coulait sous les réponses entrantes jusqu'au reload (stampSentSeq ne couvre que les envois chat).
            // seq UNIQUEMENT : status/data/read gardent le no-op historique ; unread inchangé (delta nul).
            if(existing.seq==null&&msg.seq!=null){
              return normalizeConvoForInbox(c,me.id,{messages:c.messages.map(m=>m.id===msg.id?{...m,seq:msg.seq}:m)});
            }
            if(!isMsgUpgrade(existing,msg)) return c;
            return normalizeConvoForInbox(c,me.id,{messages:c.messages.map(m=>m.id===msg.id?msg:m)});
          }
          const unreadCount=convoUnreadCount(c,me.id)+(messageUnreadForUser(msg,me.id)?1:0);
          return normalizeConvoForInbox(c,me.id,{messages:[...c.messages,msg],unreadCount});
        });
      });
      try{
        if(row.kind==="chat"&&!row.system&&row.from_id&&row.from_id!==me.id&&!toastedMsgIds.has(msg.id)
           &&document.visibilityState==="visible"&&msgToastPrefRef.current
           &&viewRef.current!=="messages"&&viewRef.current!=="inbox"){
          toastedMsgIds.add(msg.id);
          const cv=(convosRef.current||[]).find(c=>c.id===cid);
          const base={fromId:row.from_id,convoId:cid,refTitle:cv?.refTitle||"",text:msg.text||"",image:!!msg.image};
          setMsgToast(prev=>prev&&prev.convoId===cid
            ?{...prev,...base,count:(prev.count||1)+1,bump:(prev.bump||0)+1}
            :{id:"mt"+genId(),...base,count:1,bump:0});
        }
      }catch(_){}
    };
    const applyMessageUpdate=row=>{
      const msg=messageFromDB(row);
      if(!msg)return;
      const cid=row.convo_id;
      setConvos(p=>{
        if(!p.some(c=>c.id===cid)){fetchConvo(cid);return p;}
        return p.map(c=>{
          if(c.id!==cid) return c;
          const current=(c.messages||[]).find(m=>m.id===msg.id);
          const merged=reconcileMessageVersion(current,msg); // un PREVIEW (texte tronqué) ne dégrade JAMAIS un FULL
          const before=current?messageUnreadForUser(current,me.id):false;
          const after=messageUnreadForUser(merged,me.id);
          const unreadCount=Math.max(0,convoUnreadCount(c,me.id)+(after&&!before?1:!after&&before?-1:0));
          const messages=current?c.messages.map(m=>m.id===msg.id?merged:m):[...c.messages,merged];
          return normalizeConvoForInbox(c,me.id,{messages,unreadCount});
        });
      });
    };
    const applyMessageHandlers={insert:applyMessageInsert,update:applyMessageUpdate};
    applyMessageRef.current=applyMessageHandlers; // pont vers le canal `lanac:user:<me>` (effet NOTIF)
    let lastConfirmedDealsCatchUpAt=0,confirmedDealsCatchUpPromise=null;
    const CONFIRMED_DEALS_CATCHUP_COOLDOWN_MS=60000;
    const catchUpConfirmedDeals=()=>{
      const now=Date.now();
      if(cancelled||confirmedDealsCatchUpPromise||now-lastConfirmedDealsCatchUpAt<CONFIRMED_DEALS_CATCHUP_COOLDOWN_MS) return confirmedDealsCatchUpPromise;
      lastConfirmedDealsCatchUpAt=now;
      confirmedDealsCatchUpPromise=client.from("deals")
        .select("id,item_id,item_title,seller_id,buyer_id,kind,seller_confirmed,buyer_confirmed,status,ts,confirmed_at,visit_only")
        .eq("status","confirmed")
        .order("confirmed_at",{ascending:false})
        .limit(5)
        .then(({data,error})=>{
          if(cancelled) return;
          if(error){console.error("[LANAC] confirmed deals catch-up error:",error.message,error.code);return;}
          const confirmed=(data||[]).map(dealFromDB).filter(Boolean);
          if(!confirmed.length) return;
          setDeals(prev=>{
            const byId=new Map(prev.map(d=>[d.id,d]));
            confirmed.forEach(d=>byId.set(d.id,d));
            return [...byId.values()].sort((a,b)=>Number(b.ts||0)-Number(a.ts||0));
          });
          reconcileConfirmedDealsBatch(confirmed);
        })
        .catch(error=>console.error("[LANAC] confirmed deals catch-up exception:",error?.message||error))
        .finally(()=>{confirmedDealsCatchUpPromise=null;});
      return confirmedDealsCatchUpPromise;
    };
    let sub=null;
    let reconnectTimer=null;
    let reconnectAttempts=0;
    let stableTimer=null;
    const createChannel=async()=>{
      if(cancelled) return;
      if(reconnectTimer){clearTimeout(reconnectTimer);reconnectTimer=null;}
      // Jeton frais sur le socket realtime AVANT chaque (re)connexion : sinon, quand le JWT a tourné,
      // auth.uid() devient null côté serveur → postgres_changes (RLS) ne livre plus + canaux privés "Unauthorized".
      // Mirroir exact du canal conversation (qui fait déjà ce setAuth à l'ouverture). getSession() rafraîchit si expiré.
      let hasSession=null; // null = indéterminé (getSession en échec réseau) ; true/false = réponse ferme
      try{const {data}=await client.auth.getSession();const tok=data?.session?.access_token;hasSession=!!tok;if(tok&&client.realtime?.setAuth)client.realtime.setAuth(tok);}catch(e){}
      if(cancelled) return;
      // Fix B egress (11/07) — mode DORMANT : session définitivement absente (réponse ferme, PAS un échec
      // réseau) alors que l'UI est « connectée » (onglet zombie à session expirée) → un canal privé
      // partirait en « Unauthorized » à chaque tentative, indéfiniment (observé en prod, logs realtime du
      // 11/07). On ne crée RIEN et on n'arme AUCUNE retente ; le retour au 1er plan (onVisible, qui voit
      // sub=null) rappelle createChannel et réessaie une fois la session restaurée. L'ancien canal est
      // retiré proprement (neutralisé d'abord, même parade anti-râle-posthume que ci-dessous).
      if(hasSession===false){const old=sub;sub=null;if(old){try{client.removeChannel(old);}catch(e){}}return;}
      // Même cause racine que le canal listings : removeChannel() fait passer l'ANCIEN canal en CLOSED,
      // et ce râle posthume armait un nouveau createChannel → boucle de reconnexion auto-entretenue.
      // On neutralise l'ancien canal AVANT le retrait, et chaque callback vérifie myChan===sub.
      const old=sub; sub=null;
      if(old){try{client.removeChannel(old);}catch(e){}}
      // Canal-ancre par utilisateur : plus AUCUN écouteur postgres_changes (publication realtime vide). Sert
      // d'ancre au rattrapage (catchUpConfirmedDeals au SUBSCRIBED) + au backoff de reconnexion. La livraison
      // LIVE passe par le Broadcast lanac:user:<uid> (notif/deal/conversation/message). [Phase 3 : à fusionner.]
      const myChan=client.channel("lanac:messages:"+me.id);
      sub=myChan;
      myChan.subscribe((status,err)=>{
          if(cancelled||myChan!==sub) return; // canal remplacé : sourd, ses évènements de cycle de vie n'orchestrent plus rien
          if(status==="SUBSCRIBED"){
            // Anti-flapping (aligné sur le canal listings) : le backoff n'est remis à zéro qu'après 10 s de tenue.
            if(stableTimer)clearTimeout(stableTimer);
            stableTimer=setTimeout(()=>{stableTimer=null;reconnectAttempts=0;},10000);
            LANAC_DEBUG&&console.info("[LANAC] realtime channel connected",{meId:me.id});
            //  A42 (13/10) — LA LIAISON EST RÉTABLIE : le serveur vient de répondre sur ce canal. C'est le signal qui ne ment pas
            //   (`navigator.onLine` dit « un routeur », pas « Internet ») : les messages qu'une panne retenait repartent
            //   (useOutboxReplay). Rien à envoyer : aucune requête.
            try{window.dispatchEvent(new Event("lanac:liaison"));}catch(_){}
            catchUpConfirmedDeals();
            if(reprendreAuCanal){ reprendreAuCanal=false; reprendre(); }   // A36 · 2d : la reprise APRÈS la reconnexion (rien entre les deux n'est perdu)
          }
          if(status==="CHANNEL_ERROR"||status==="TIMED_OUT"||status==="CLOSED"){
            console.warn("[LANAC] global realtime channel dropped:",status,err?.message||"",{attempt:reconnectAttempts});
            if(stableTimer){clearTimeout(stableTimer);stableTimer=null;}
            if(reconnectTimer){clearTimeout(reconnectTimer);reconnectTimer=null;}
            if(!cancelled){
              reconnectAttempts++;
              // Retentes sans abandon, plafonnées à 30 s (aligné sur le canal listings) : messagerie
              // jamais morte en silence, coût d'un essai = un handshake WS.
              const delay=Math.min(1000*Math.pow(2,Math.min(reconnectAttempts,6)-1),30000);
              LANAC_DEBUG&&console.info("[LANAC] realtime reconnect scheduled",{delay,attempt:reconnectAttempts,meId:me.id});
              reconnectTimer=setTimeout(createChannel,delay);
            }
          }
        });
    };
    createChannel();
    // A1 — kill-switch (défaut ON) + délai avant libération (défaut 60 s), lus LIVE (toggle sans redéploiement).
    const bgDisconnectOn=()=>{try{return localStorage.getItem("lanac:rtBackgroundDisconnect")!=="0";}catch(_){return true;}};
    const bgHiddenDelayMs=()=>{try{const v=Number(localStorage.getItem("lanac:rtBgDisconnectDelayMs"));return Number.isFinite(v)&&v>=0?v:60000;}catch(_){return 60000;}};
    let suspendTimer=null;
    // Onglet resté caché au-delà du délai : on RELÂCHE les 2 canaux persistants (ancre + livraison). Le
    // canal-ancre est neutralisé AVANT retrait (sub=null ⇒ son callback CLOSED devient sourd via myChan!==sub
    // ⇒ AUCUNE reconnexion planifiée pendant l'absence) et les timers de reconnexion en vol sont coupés. Puis
    // on retire `lanac:user` via le pont ref. channels.length→0 ⇒ supabase-js programme la fermeture du socket
    // (~60 s) ⇒ la connexion est relâchée et l'utilisateur backgroundé cesse de compter dans le plafond
    // Supabase. Les push OS passent par le Service Worker (pas par ce WS) ⇒ aucune notif perdue en arrière-plan.
    const suspendChannels=()=>{
      if(reconnectTimer){clearTimeout(reconnectTimer);reconnectTimer=null;}
      if(stableTimer){clearTimeout(stableTimer);stableTimer=null;}
      const old=sub;sub=null;if(old){try{client.removeChannel(old);}catch(e){}}
      try{bcastTeardownRef.current&&bcastTeardownRef.current();}catch(e){}
      LANAC_DEBUG&&console.info("[LANAC] realtime suspended (onglet caché)",{meId:me.id});
    };
    // ── A36 · 2d (12/10) : LA REPRISE — un curseur, un appel (src/hooks/reprise.jsx ; migrations 20261012135731 et 135958) ──
    //  Au retour d'une absence (lanac:present ≥ 60 s) ou quand le canal était tombé : UN appel lanac_reprise(depuis, fil ouvert)
    //  → notifications, fils touchés, messages MODIFIÉS du fil ouvert, compteurs ; le curseur devient l'horloge du serveur.
    //  Curseur initial : l'instant du démarrage moins 5 min (une dérive d'horloge du poste ne fait rien manquer ; rejouer une
    //  notification déjà connue est sans effet). Une section qui déborde demande son rechargement complet (toutRelire).
    //  Une seule reprise en vol à la fois ; un retour au premier plan de quelques secondes (canal sain) n'en demande pas.
    //  D2 (relecture) : l'effet peut se rejouer (rôle, profil) alors qu'un fil est déjà ouvert — le messenger laisse aussi le fil
    //  ouvert sur la fenêtre (window.__lncFilOuvert), lu ici au départ ; l'événement tient ensuite la valeur à jour.
    let curseurReprise=Date.now()-300000, repriseEnVol=null, filOuvert=(typeof window!=="undefined"&&window.__lncFilOuvert)||null;
    //  relecture du soir : quand le canal était tombé, la reprise repart UNE fois de plus à sa réinscription (SUBSCRIBED) — un
    //  message écrit entre l'instantané de la première reprise et la réinscription n'est perdu par aucune voie (Discord rejoue
    //  APRÈS la reconnexion) ; le curseur a avancé, l'appel est petit
    let reprendreAuCanal=false;
    const onFilOuvert=(ev)=>{ try{ filOuvert=(ev&&ev.detail&&ev.detail.id)||null; }catch(e){} };
    window.addEventListener("lanac:fil-ouvert",onFilOuvert);
    const reprendre=()=>{
      if(cancelled||repriseEnVol) return repriseEnVol;
      const depuis=curseurReprise, fil=filOuvert;
      repriseEnVol=rpcReprise(depuis,fil).then(({data,error})=>{
        repriseEnVol=null;
        if(cancelled) return;
        if(error){ console.error("[LANAC] reprise error:",error.message||error,error.code); loadConvos(); return; }   // le filet : la reprise a échoué ⇒ rechargement complet
        const cr=appliquerReprise(data,{ me, actId:fil, setConvos,
          appliquerNotification:(row)=>{ if(applyNotifRef.current) applyNotifRef.current(row); },
          // « le reste de 2d » (12/10) : les fils touchés sont REJOUÉS par les voies du direct — la ligne du fil (mergeConvo), son dernier
          //  message allégé (applyMessageInsert : un fil inconnu est lu par son id, borné), ses non-lus (sans toucher aux autres fils)
          fusionnerFil:(row)=>{ try{ mergeConvo(row); }catch(e){} },
          //  relecture du soir : par la voie de MISE À JOUR (un dernier message déjà connu dont le statut a changé, un retrait) — absent ⇒ ajouté,
          //  fil inconnu ⇒ lu par son id ; le filigrane (lu ailleurs) est posé AVANT le compte, jamais en recul
          dernierMessage:(row)=>{ try{ applyMessageUpdate(row); }catch(e){} },
          filigrane:(map)=>{ map.forEach((rs,id)=>{ lanacReadSeqMap.set(id,Math.max(Number(lanacReadSeqMap.get(id)||0),rs)); }); },
          nonLus:(map)=>{ if(cancelled) return; setConvos(prev=>prev.map(c=>map.has(String(c.id))?normalizeConvoForInbox(c,me.id,{unreadCount:map.get(String(c.id))}):c)); },
          compteurs:(j)=>{ setLanacInboxServerCounters(j); if(setInboxServerCounters) setInboxServerCounters(lanacInboxServerCounters.value); },
          toutRelire:(k)=>{ if(k==="notifications"){ if(reloadNoticesRef.current) reloadNoticesRef.current(); } else if(k==="fils") loadConvos(); else if(k==="messages"&&fil) fetchConvo(fil,true); } });
        if(cr.curseur>0) curseurReprise=cr.curseur-REPRISE_MARGE_MS;   // D1 : une transaction validée après l'instantané n'est pas perdue
        lastConvosLoadAt=Date.now();   // la reprise a tenu lieu de rechargement : la fenêtre de 60 s repart d'ici
        LANAC_DEBUG&&console.info("[LANAC] reprise",{depuis,fil,...cr});
      },(e)=>{ repriseEnVol=null; console.error("[LANAC] reprise exception:",(e&&e.message)||e); if(!cancelled) loadConvos(); });
      return repriseEnVol;
    };
    const onVisibility=(ev)=>{
      if(cancelled) return;
      const retourDAbsence=(ev===true);   // A36 · 2d : appelé par lanac:present (absence ≥ 60 s) — jamais par un simple visibilitychange
      if(document.visibilityState!=="visible"){
        // Passage en arrière-plan : armer la libération différée (annulée si retour avant l'échéance → 0 churn
        // sur les bascules d'onglet courtes). Kill-switch OFF ⇒ comportement historique (WS jamais relâché).
        if(!bgDisconnectOn()) return;
        if(suspendTimer) clearTimeout(suspendTimer);
        suspendTimer=setTimeout(()=>{suspendTimer=null;if(!cancelled&&document.visibilityState!=="visible")suspendChannels();},bgHiddenDelayMs());
        return;
      }
      // Retour au 1er plan : annuler toute libération en attente, PUIS reprise (logique historique inchangée).
      if(suspendTimer){clearTimeout(suspendTimer);suspendTimer=null;}
      const _lc=lanacSupabaseClient();
      // Vérifie la session quand l'onglet redevient visible — getSession() déclenche le refresh automatique si nécessaire (avec lock interne Supabase, pas de conflit de rotation)
      if(_lc) _lc.auth.getSession().catch(()=>{});
      // Fix D egress (12/07) : au retour au 1er plan, on ne recharge l'inbox QUE si (a) le canal realtime
      // était tombé — un message a pu être manqué pendant l'absence, le badge non-lus doit être corrigé
      // sans délai (garde anti-gap, priorité fraîcheur) — OU (b) le dernier chargement date de ≥60 s.
      // Canal sain + refetch récent = le direct a déjà tout livré → rien à refaire, on évite la rafale de
      // fetchs inbox mesurée à chaque bascule d'onglet. Le pull-to-refresh manuel reste TOUJOURS immédiat.
      const channelDown=!sub||sub.state==="closed"||sub.state==="errored";
      // A36 · 2d : la reprise (un appel : notifications, fils touchés REJOUÉS avec leur dernier message et leurs non-lus, cartes
      //  modifiées du fil ouvert, compteurs) — au retour d'une absence, ou quand le canal était tombé (l'onglet caché plus d'une
      //  minute l'a relâché). « Le reste de 2d » (12/10, mesuré par S155) : elle REMPLACE le rechargement complet de la liste
      //  des fils, qui ne sert plus qu'au débordement (fils_trop), à l'échec de la reprise, et au vieux cas d'un canal sain
      //  resté sans rechargement depuis 60 s (kill-switch du relâchement à l'arrêt).
      if(channelDown||retourDAbsence) reprendre();
      else if(Date.now()-lastConvosLoadAt>=60000) loadConvos();
      catchUpConfirmedDeals();
      if(channelDown){
        reconnectAttempts=0;
        reprendreAuCanal=true;
        createChannel();
      }
      // Recrée `lanac:user` (idempotent : no-op s'il n'a jamais été relâché, ex. bascule courte). Le canal-ancre
      // est déjà recréé ci-dessus par createChannel() quand channelDown.
      try{bcastRearmRef.current&&bcastRearmRef.current();}catch(e){}
    };
    document.addEventListener("visibilitychange",onVisibility);
    // A36 · 2b (11/10) : le membre revient après une absence (l'« au revoir » a coupé la diffusion sur lanac:user aussitôt) — même
    //  rattrapage qu'un retour au premier plan (la boîte relue si le canal est tombé ou si elle date de 60 s, les accords) dès
    //  60 s d'absence ; en deçà le trou est celui d'un onglet caché moins d'une minute, connu et assumé (Fix D egress, 12/07).
    const onPresent=(ev)=>{ try{ if(Number(ev&&ev.detail&&ev.detail.absence)>=PRESENCE_RATTRAPAGE_MS) onVisibility(true); }catch(e){} };
    window.addEventListener("lanac:present",onPresent);
    return()=>{cancelled=true;window.removeEventListener("lanac:present",onPresent);window.removeEventListener("lanac:fil-ouvert",onFilOuvert);if(applyDealRef.current===applyDealRow)applyDealRef.current=null;if(mergeConvoRef.current===mergeConvo)mergeConvoRef.current=null;if(applyMessageRef.current===applyMessageHandlers)applyMessageRef.current=null;document.removeEventListener("visibilitychange",onVisibility);if(suspendTimer)clearTimeout(suspendTimer);if(reconnectTimer)clearTimeout(reconnectTimer);if(stableTimer)clearTimeout(stableTimer);const c=sub;sub=null;if(c){try{client.removeChannel(c);}catch(e){}}};
  },[me?.id,me?.role,me?.gen]);
  useEffect(()=>{
    const client=lanacSupabaseClient();
    if(!client||!me?.id){ if(!me) setNotices([]); return; }
    let cancelled=false;
    const toNotice=row=>({ id:row.id, type:row.type, titleI18n:row.payload?.title??"", textI18n:row.payload?.text??"",
      color:row.payload?.color||"var(--color-success)", target:row.payload?.target||null,
      audience:me.id, read:!!row.read, ts:Number(row.ts)||0, riche:richNoticeFromRow(row) });
    // 12/07 : 25 au boot (au lieu de 50) — la cloche affiche rarement plus, egress du boot ÷2 ;
    // l'historique complet reste accessible via « Voir plus » (chargé uniquement au tap).
    const chargerNotices=()=>client.from("notifications").select("*").eq("user_id",me.id).order("ts",{ascending:false}).limit(25).then(({data,error})=>{
      if(cancelled||error||!Array.isArray(data)) return;
      setNotices(data.map(toNotice));
      setNoticesHasMore(data.length===25);
    });
    chargerNotices();
    reloadNoticesRef.current=chargerNotices;   // A36 · 2d : la reprise a débordé (plus de 50 notifications) ⇒ la liste se recharge
    // Handlers PARTAGÉS : postgres_changes ET Broadcast appliquent EXACTEMENT la même logique.
    // 12/07 : le serveur COLLAPSE les rafales de messages en réutilisant la même ligne (payload/ts
    // changent). ts plus récent => contenu rafraîchi ET remontée en tête ; sinon (marquage lu/non-lu)
    // => mise à jour en place, ordre intact.
    // ── COUP DE GRÂCE (30/08) · compte suspendu = session coupée SUR-LE-CHAMP ──
    //  Le serveur (admin_set_user_blocked) a déjà tout tué : écritures (garde 28/08),
    //  re-login et refresh (banned_until GoTrue), sessions (cascade). Ce handler ferme
    //  la dernière fenêtre — le JWT d'accès en cours (≤1 h, lecture seule) : à la
    //  notification realtime `account_blocked`, on purge la session LOCALE et on
    //  recharge vers l'écran d'auth (où GoTrue refusera le re-login). Un broadcast
    //  unique au blocage, ZÉRO coût récurrent, zéro requête ajoutée au cas nominal.
    const lncKickIfBlocked=(row)=>{
      if(!row||row.type!=="account_blocked"||window.__lncKickDone) return false;
      window.__lncKickDone=true;
      Promise.resolve(lanacSortie({scope:"local"},me.id)).catch(()=>{}).then(()=>{try{window.location.reload();}catch(_){}});   // (B-HORS-LIGNE-1) la carte oubliée avec la session
      return true;
    };
    const applyNotifInsert=(row)=>{ if(!row) return; if(lncKickIfBlocked(row)) return; setNotices(p=> p.some(n=>n.id===row.id||n.id===row.dedup_key) ? p : [toNotice(row),...p].slice(0,200)); };
    const applyNotifUpdate=(row)=>{ if(!row) return; setNotices(p=>{
      const cur=p.find(n=>n.id===row.id);
      if(cur&&Number(row.ts)>cur.ts) return [toNotice(row),...p.filter(n=>n.id!==row.id)].slice(0,200);
      if(cur) return p.map(n=>n.id===row.id?{...n,...toNotice(row)}:n);
      if(!row.read) return [toNotice(row),...p].slice(0,200); // bulle recyclée au-delà des pages chargées
      return p;
    }); };
    // A36 · 2d (12/10) : une notification venue de la REPRISE prend la même voie que le direct — inconnue : insérée ; connue et
    //  plus récente (ts) : rafraîchie et remontée ; connue, même instant : mise à jour en place (lu / non lu). Idempotent.
    const applyNotifReprise=(row)=>{ if(!row||lncKickIfBlocked(row)) return; setNotices(p=>{
      const cur=p.find(n=>n.id===row.id);
      if(!cur) return p.some(n=>n.id===row.dedup_key)?p:[toNotice(row),...p].slice(0,200);
      if(Number(row.ts)>cur.ts) return [toNotice(row),...p.filter(n=>n.id!==row.id)].slice(0,200);
      return p.map(n=>n.id===row.id?{...n,...toNotice(row)}:n);
    }); };
    applyNotifRef.current=applyNotifReprise;
    let bcast=null; // canal notif:<uid> (postgres_changes) RETIRÉ — livraison via le Broadcast lanac:user:<uid>
    // ci-dessous (trigger broadcast_notification) + chargement au boot (25 dernières) pour le rattrapage.
    // Realtime moderne #3 — Phase 1 (ADDITIF, double-run) : diffusion Broadcast sur le topic PRIVÉ
    // `lanac:user:<me.id>` (trigger serveur broadcast_notification → realtime.send). Autorisation :
    // RLS realtime.messages (seul l'user rejoint son topic). Le dédup par id neutralise le doublon
    // postgres_changes + broadcast → ZÉRO doublon, ZÉRO régression. Retrait de postgres_changes après rodage.
    let creating=false;
    // A1 — (re)création IDEMPOTENTE du canal de livraison, exposée à l'effet MESSAGES via bcastRearmRef pour la
    // reprise au 1er plan (no-op si déjà en place / en cours : bcast||creating). Setup du canal INCHANGÉ.
    const createBcast=async()=>{
      if(cancelled||creating||bcast) return;
      creating=true;
      try{ const {data}=await client.auth.getSession(); const tok=data?.session?.access_token; if(tok&&client.realtime?.setAuth) client.realtime.setAuth(tok); }catch(_){}
      if(cancelled){creating=false;return;}
      try{
        bcast=client.channel("lanac:user:"+me.id,{config:{private:true}})
          .on("broadcast",{event:"notification"},msg=>{ const row=msg?.payload?.record; if(!row||String(row.user_id)!==String(me.id)) return; if(msg.payload.op==="INSERT"){ applyNotifInsert(row);
            // A36 · 2c-1 bis (11/10) : l'ÉVÉNEMENT plutôt que l'attente — quand la passe serveur a écrit une échéance d'annonce
            //  (réservation échue, demande close, épingle orpheline), sa notification arrive ici à l'instant : on demande AUSSITÔT la
            //  relecture bornée de la grille (useFeedState · catchUp). La relecture minutée de la réconciliation reste le filet.
            if(/^(resv_expired|nores|orphan_pin):/.test(String(row.dedup_key||""))){ try{window.dispatchEvent(new Event("lanac:relire-annonces"));}catch(_){} } }
            else applyNotifUpdate(row); })
          // Phase 2a (ADDITIF, double-run) : deals sur MON topic (trigger broadcast_deal → vendeur+acheteur).
          // Défense en profondeur : je ne suis destinataire QUE si je suis partie au deal (l'autorisation RLS
          // du topic le garantit déjà). Applique via le pont ref → logique IDENTIQUE à postgres_changes (dédup).
          .on("broadcast",{event:"deal"},msg=>{ const row=msg?.payload?.record; if(!row||(String(row.seller_id)!==String(me.id)&&String(row.buyer_id)!==String(me.id))) return; if(applyDealRef.current) applyDealRef.current(row); })
          // Phase 2b (ADDITIF, double-run) : conversations sur MON topic (trigger broadcast_conversation
          // → chaque participant). mergeConvo est auto-gardé (canSeeConvo) + idempotent → même voie que
          // postgres_changes, ZÉRO doublon. Admin garde sa vue globale via postgres_changes (non retiré).
          .on("broadcast",{event:"conversation"},msg=>{ const row=msg?.payload?.record; if(row&&mergeConvoRef.current) mergeConvoRef.current(row); })
          // Phase 2c (ADDITIF, double-run) : messages sur MON topic (voie INBOX — trigger broadcast_message
          // → chaque participant). Handlers idempotents (setConvos dédup + toast dédupliqué par id) → même
          // voie que postgres_changes. Le fil OUVERT reste sur lanac:convo:<id> (messenger), non touché.
          .on("broadcast",{event:"message"},msg=>{ const h=applyMessageRef.current; const row=msg?.payload?.record; if(!row||!h) return; if(msg.payload.op==="UPDATE") h.update(row); else h.insert(row); })
          .subscribe();
      }catch(_){}
      creating=false;
    };
    // A1 — retrait du canal de livraison (relâche le socket avec l'ancre quand l'onglet reste caché), exposé via
    // bcastTeardownRef. Ne touche PAS `cancelled` : l'effet reste vivant, le canal est recréable via rearm.
    const teardownBcast=()=>{ const c=bcast; bcast=null; if(c){try{client.removeChannel(c);}catch(_){}} };
    createBcast();
    bcastRearmRef.current=createBcast;
    bcastTeardownRef.current=teardownBcast;
    return ()=>{ cancelled=true; if(applyNotifRef.current===applyNotifReprise)applyNotifRef.current=null; if(reloadNoticesRef.current===chargerNotices)reloadNoticesRef.current=null; if(bcastRearmRef.current===createBcast)bcastRearmRef.current=null; if(bcastTeardownRef.current===teardownBcast)bcastTeardownRef.current=null; teardownBcast(); };
  },[me?.id,me?.gen]);
}
export { useRealtimeInbox };
